import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { toast } from "sonner";
import { Modal } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { createAcademicYear } from "../../../api/generalParam";
import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";

type Props = {
  show: boolean;
  onClose: () => void;
  onSaved: () => void;
};

const CreateAcademicYearModal = ({ show, onClose, onSaved }: Props) => {
  const labelSchema = z
    .string()
    .regex(/^\d{4}-\d{4}$/, "Le format doit être AAAA-AAAA, ex: 2024-2025")
    .nonempty("Libélle requis pour l'année académique")
    .refine(
      (val) => {
        const [start, end] = val.split("-").map(Number);
        return start + 1 === end;
      },
      {
        message: "Les années doivent être consécutives, ex: 2024-2025",
      },
    );

  const createSchema = z.object({
    label: labelSchema,
    startDate: z.string().nonempty("Date de début requis"),
    endDate: z.string().nonempty("Date de fin requis"),
    statut: z.boolean(),
  });

  type FormFields = z.infer<typeof createSchema>;
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      statut: false, // valeur par défaut cochée
    },
  });

  //Traitement après validation du formulaire de création d'. année académique
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      //Envoi des datas pour création vers le backend
      await createAcademicYear(data);

      reset(); //On réinitialise les champs du formulaire
      if (onSaved) onSaved(); // On fait un reload de la liste

      onClose(); // On ferme le modal
      window.location.reload();
    } catch (error: any) {
      // console.log(error)
      // Si erreur (Toast Error)
      toast.error(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Une erreur s'est produit, Réessayer plus tard",
      );

    }
  };

  return (
    <>
      <Modal show={show} onHide={() => onClose()}>
        <Modal.Header closeButton>
          <Modal.Title>Nouvelle Année Académique</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form
            id="academic-year-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row row-cols-xxl-5 row-cols-md-6">
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Libellé
                  </label>
                  <input
                    {...register("label")}
                    type="text"
                    className={
                      errors.label ? "form-control is-invalid" : "form-control"
                    }
                    placeholder="Format : AAAA-AAAA"
                  />
                  {errors.label && (
                    <div className="invalid-feedback">
                      {errors.label.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer02">
                    Date Début
                  </label>
                  <input
                    {...register("startDate")}
                    type="date"
                    className={
                      errors.startDate
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Début de l'année"
                  />
                  {errors.startDate && (
                    <div className="invalid-feedback">
                      {errors.startDate.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer03">
                    Date Fin
                  </label>
                  <input
                    {...register("endDate")}
                    type="date"
                    className={
                      errors.endDate
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Fin de l'année"
                  />
                  {errors.endDate && (
                    <div className="invalid-feedback">
                      {errors.endDate.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="form-check form-check-lg form-switch mb-3">
                  <label className="form-check-label" htmlFor="switch-lg">
                    Statut de l'année
                  </label>
                  <input
                    {...register("statut")}
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="switch-lg"
                  />
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Annuler
          </button>

          {isSubmitting ? (
            <button
              className="btn btn-primary-light"
              type="button"
              disabled={false}
            >
              <span
                className="spinner-border spinner-border-sm align-middle me-1"
                role="status"
                aria-hidden="true"
              />
              Chargement...
            </button>
          ) : (
            <button
              type="submit"
              form="academic-year-form" // 🔗 liaison au formulaire
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              Enregistrer
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  CreateAcademicYearModal,
  [buildPermission(Resource.ACADEMIC_YEAR, Action.CREATE)],
  "masquer",
);
